import { Component, Input, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PiiDecryptService } from 'src/app/_services/pii-decrypt.service';
import { PiiAuditService, PiiAuditRequest } from 'src/app/_services/pii-audit.service';
import { PiiFormService } from 'src/app/_services/pii-form.service';
import { PiiUnhideModalComponent } from '../pii-unhide-modal/pii-unhide-modal.component';
import { AlertService } from 'src/app/_services/alert.service';
import { StorageService } from 'src/app/_services/storage.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-pii-field',
  templateUrl: './pii-field.component.html',
  styleUrls: ['./pii-field.component.scss']
})
export class PiiFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() fieldName: string = '';
  @Input() encryptedValue: string = '';
  @Input() userId: string = '';
  @Input() label: string = '';
  @Input() maskPattern: string = 'XXX-XXXX-XXXXXXX-X'; // Default for Emirates ID
  @Input() visibleStart: number = 3;
  @Input() visibleEnd: number = 1;
  @Input() showLabel: boolean = true;
  @Input() readonly: boolean = false;
  
  // Enhanced entity context for audit logging
  @Input() entityType: string = 'customer'; // customer, merchant, reseller, transaction, etc.
  @Input() entityMobile: string = ''; // Contact number
  @Input() transactionId: string = ''; // Transaction ID for transaction-related PII
  @Input() referenceId: string = ''; // Generic reference ID (transaction, order, case, etc.)

  isUnmasked: boolean = false;
  decryptedValue: string = '';
  maskedValue: string = '';
  isLoading: boolean = false;

  private unmaskSubscription: Subscription;

  constructor(
    private modalService: NgbModal,
    private piiDecryptService: PiiDecryptService,
    private piiAuditService: PiiAuditService,
    private piiFormService: PiiFormService,
    private alertService: AlertService,
    private storageService: StorageService,
    private router: Router
  ) {
    // Subscribe to form-level unmask state
    this.unmaskSubscription = this.piiFormService.formUnmasked$.subscribe((isUnmasked) => {
      this.isUnmasked = isUnmasked;
    });
  }

  ngOnInit(): void {
    this.decryptAndMaskData();
  }

  ngOnChanges(): void {
    this.decryptAndMaskData();
  }

  ngOnDestroy(): void {
    if (this.unmaskSubscription) {
      this.unmaskSubscription.unsubscribe();
    }
  }

  /**
   * Decrypt the encrypted data and generate masked version on component load
   * Updated for unified encryption approach: all encrypted data decrypts to strings
   */
  private decryptAndMaskData(): void {
    if (this.encryptedValue != null && this.encryptedValue !== '') {
      const originalString = this.encryptedValue.toString();
      
      // Check if this is a plain numeric value (not encrypted - legacy case)
      if (typeof this.encryptedValue === 'number' || 
         (typeof this.encryptedValue === 'string' && this.isNumericString(originalString) && !this.piiDecryptService.isEncrypted(originalString))) {
        // Handle plain numeric values - just mask them
        this.decryptedValue = originalString;
        this.maskedValue = this.maskNumericValue(originalString);
      }
      // Check if this looks like encrypted data (unified approach)
      else if (typeof this.encryptedValue === 'string' && this.piiDecryptService.isEncrypted(originalString)) {
        // Decrypt the data - will always return a string
        const decryptedValue = this.piiDecryptService.decrypt(originalString);
        
        // Check if decryption failed
        if (decryptedValue === '[DECRYPTION ERROR]' || !decryptedValue) {
          // Fallback: use the encrypted value itself for masking
          this.decryptedValue = originalString;
          this.maskedValue = this.createFallbackMask(originalString);
          console.error('PII field decryption failed:', this.fieldName);
        } else {
          this.decryptedValue = decryptedValue;
          
          // Check if decrypted string represents a numeric value (BigDecimal amount)
          if (this.isNumericString(decryptedValue)) {
            // This is an encrypted amount - mask it as numeric
            this.maskedValue = this.maskNumericValue(decryptedValue);
          }
          // Check if it's other PII data (Emirates ID, names, etc.)
          else if (this.containsWeirdCharacters(decryptedValue)) {
            this.maskedValue = this.createFallbackMask(decryptedValue);
          } else {
            // Check for Emirates ID field
            if (this.fieldName.toLowerCase().includes('emiratesid') || this.fieldName.toLowerCase().includes('eid')) {
              this.maskedValue = 'X'.repeat(18);
            } else {
              // Regular PII data - use standard masking with custom visibility
              this.maskedValue = this.piiDecryptService.getMaskedData(
                decryptedValue, 
                this.visibleStart, 
                this.visibleEnd
              );
            }
          }
        }
      } else {
        // Plain text data (not encrypted)
        this.decryptedValue = originalString;
        if (this.containsWeirdCharacters(originalString)) {
          this.maskedValue = this.createFallbackMask(originalString);
        } else {
          // Check for Emirates ID field
          if (this.fieldName.toLowerCase().includes('emiratesid') || this.fieldName.toLowerCase().includes('eid')) {
            this.maskedValue = 'X'.repeat(18);
          } else {
            // Use normal masking with custom visibility
            this.maskedValue = this.piiDecryptService.getMaskedData(
              originalString, 
              this.visibleStart, 
              this.visibleEnd
            );
          }
        }
      }
    } else {
      // Handle null/empty values
      this.decryptedValue = '';
      this.maskedValue = '';
    }
  }

  /**
   * Check if a value is a numeric string (including BigDecimal format)
   */
  private isNumericString(str: string): boolean {
    if (!str || str.trim() === '') return false;
    
    // Check for valid number patterns including decimals and negative numbers
    const numericPattern = /^-?\d+(\.\d+)?$/;
    return numericPattern.test(str.trim());
  }

  /**
   * Create masked version for numeric values (amounts) - completely hide the number
   */
  private maskNumericValue(value: string): string {
    if (!value || value.trim() === '') return '';
    
    const cleanValue = value.trim();
    
    // For numeric values, completely mask them for security
    if (cleanValue.includes('.')) {
      const [integerPart, decimalPart] = cleanValue.split('.');
      // Mask integer part completely, show .XX for decimal part
      return 'X'.repeat(Math.max(integerPart.length, 3)) + '.XX';
    } else {
      // For integer amounts, mask completely
      return 'X'.repeat(Math.max(cleanValue.length, 3));
    }
  }

  /**
   * Check if a string contains weird/non-printable characters
   */
  private containsWeirdCharacters(str: string): boolean {
    // Check for non-printable ASCII characters (except common whitespace)
    const hasNonPrintable = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/.test(str);
    
    // Check for too many special characters
    const specialCharCount = (str.match(/[^\w\s.-]/g) || []).length;
    const hasExcessiveSpecialChars = specialCharCount > str.length * 0.3;
    
    return hasNonPrintable || hasExcessiveSpecialChars;
  }

  /**
   * Create a fallback mask for data with weird characters
   */
  private createFallbackMask(data: string): string {
    if (!data || data.trim() === '') {
      return '';
    }
    
    const cleanValue = data.replace(/[^0-9]/g, '');
    
    if (this.fieldName.toLowerCase().includes('emiratesid') || this.fieldName.toLowerCase().includes('eid')) {
      return 'X'.repeat(18);
    }
    
    if (cleanValue.length >= 15) {
      return 'X'.repeat(18);
    } else if (cleanValue.length >= 10) {
      return 'XXX-XXXX-XXX';
    } else if (cleanValue.length >= 5) {
      return 'XX-XXX-XX';
    } else {
      return 'X'.repeat(Math.max(cleanValue.length, 3));
    }
  }

  /**
   * Handle unhide button click - unmasks all PII fields in the form
   */
  onUnhide(): void {
    if (this.readonly) {
      return;
    }

    // Use form service to unmask all PII fields in the form
    this.piiFormService.unmaskAllFields(this.userId || 'unknown', this.getEffectiveEntityType());
  }

  /**
   * Process the unhide request with audit logging - just unmask the data
   */
  private processUnhideRequest(auditData: any): void {
    this.isLoading = true;
    
    const currentUser = this.storageService.getUserName();
    const auditRequest: PiiAuditRequest = {
      userId: this.userId || 'unknown',
      fieldName: this.fieldName,
      reasonId: auditData.reasonId,
      reasonDescription: auditData.reason || 'Unknown reason', // Include human-readable description
      comment: auditData.comment,
      accessedBy: currentUser || 'unknown',
      timestamp: new Date().toISOString(),
      context: 'field', // Indicate this is from a form field
      
      // Enhanced entity context for audit logging with transaction support
      entityType: this.getEffectiveEntityType(),
      entityMobile: this.entityMobile,
      transactionId: this.transactionId,
      referenceId: this.getReferenceId()
    };





    this.piiAuditService.logPiiAccess(auditData).subscribe({
      next: (response) => {
        // Simply set unmask state - the getter will handle showing decrypted value
        this.isUnmasked = true;
        this.alertService.successAlertWithMessage('PII data unmasked successfully');
      },
      error: (error) => {
        // Even if audit logging fails, allow unmask for demo purposes
        this.isUnmasked = true;
        this.alertService.successAlertWithMessage('PII data unmasked successfully ');
      }
    });
      }    /**
   * Hide/mask all PII fields in the form
   */
  onHide(): void {
    this.isLoading = false;
    this.piiFormService.maskAllFields();
  }

  /**
   * Copy decrypted value to clipboard
   */
  onCopy(): void {
    if (this.decryptedValue) {
      navigator.clipboard.writeText(this.decryptedValue).then(() => {
        this.alertService.toastSuccessMessageAlert({
          title: 'Copied'
        });
      }).catch(() => {
        this.alertService.toastErrorMessageAlert({
          title: 'Copy Failed'
        });
      });
    }
  }

  /**
   * Get display value based on current state
   */
  get displayValue(): string {
    if (!this.encryptedValue || this.encryptedValue.toString().trim() === '') {
      return '';
    }
    
    let result = this.isUnmasked ? this.decryptedValue : this.maskedValue;
    
    if (this.isUnmasked && (this.fieldName.toLowerCase().includes('emiratesid') || this.fieldName.toLowerCase().includes('eid'))) {
      result = this.formatEmiratesIdForDisplay(result);
    }
    
    return result;
  }

  private formatEmiratesIdForDisplay(value: string): string {
    if (!value) return '';
    
    const cleanValue = value.replace(/[^0-9]/g, '');
    
    if (cleanValue.length === 15) {
      return `${cleanValue.slice(0, 3)}-${cleanValue.slice(3, 7)}-${cleanValue.slice(7, 14)}-${cleanValue.slice(14)}`;
    }
    
    return value;
  }

    /**
   * Check if field can be unhidden
   */
  get canUnhide(): boolean {
    return !this.readonly && !this.isUnmasked && !!this.encryptedValue && this.encryptedValue.length > 0;
  }

  /**
   * Get effective entity type - use provided entityType or auto-detect from route
   */
  private getEffectiveEntityType(): string {
    // If entityType is explicitly provided and not the default, use it
    if (this.entityType && this.entityType !== 'customer') {
      return this.entityType;
    }
    
    // Auto-detect based on current route
    const currentRoute = this.router.url.toLowerCase();
    
    if (currentRoute.includes('transaction')) {
      return 'transaction';
    } else if (currentRoute.includes('merchant')) {
      return 'merchant';
    } else if (currentRoute.includes('reseller')) {
      return 'reseller';
    } else if (currentRoute.includes('customer')) {
      return 'customer';
    }
    
    // Fall back to provided entityType or default
    return this.entityType || 'customer';
  }

  /**
   * Get the appropriate reference ID based on context
   */
  private getReferenceId(): string {
    // Priority order: explicit referenceId > transactionId > userId
    if (this.referenceId) {
      return this.referenceId;
    }
    
    if (this.transactionId) {
      return this.transactionId;
    }
    
    // For transaction contexts, prefer transaction ID over user ID
    const effectiveEntityType = this.getEffectiveEntityType();
    if (effectiveEntityType === 'transaction' && this.transactionId) {
      return this.transactionId;
    }
    
    // Fall back to userId for non-transaction contexts
    return this.userId || 'unknown';
  }

  }  